import type { JSX } from 'react';
import { Badge, FormSection, Radio } from '@ksyos/design-system';
import type { BariPreMdoReportData } from '@common/validation/customControls/bariPre/bariPreMdoReport/v1/bariPreMdoReportSchemaV1';
import { MdoReportAdvice, mdoReportAdviceWithColor } from '@common/customControls/bariatrics';
import { useGetCaroPatient } from '@frontend/utils/hooks/useGetCaroPatient';
import { useProcessActivityEditContext } from '@frontend/utils/hooks/useProcessActivityContext';
import { AlertCheckbox } from '../../AlertCheckbox';
import {
    BooleanRadioGroupFormGroup,
    DateInputFormGroup,
    RadioGroupFormGroup,
    TextAreaFormGroup,
} from '../../processActivityFormGroupFields';
import { AanvullendeConclusies } from '../AanvullendeConclusies';
import { CaroActivatedAccountAlert } from '../CaroActivatedAccountAlert';
import { IndicatiesVoorVerwijzing } from '../IndicatiesVoorVerwijzing';
import { adviceValueIsDisabled, adviesAanFields } from '../utils';

export function BariPreMdoReportEdit(): JSX.Element {
    const { data, errors, onChange } = useProcessActivityEditContext<BariPreMdoReportData>();
    const caroPatient = useGetCaroPatient();

    return (
        <>
            {!caroPatient.isLoading && (
                <CaroActivatedAccountAlert hasActivatedAccount={caroPatient.hasActivatedAccount} />
            )}
            <FormSection>
                <DateInputFormGroup
                    fieldKey="datum_mdo"
                    isRequired={true}
                    label="Datum MDO"
                />
                <TextAreaFormGroup
                    fieldKey="verslag_bespreking_mdo"
                    isRequired={true}
                    label="Verslag bespreking MDO"
                    placeholder="Typ je verslag..."
                />
            </FormSection>

            <FormSection title="Conclusie">
                <RadioGroupFormGroup
                    fieldKey="inclusie_in_zorgpad"
                    isRequired={true}
                    label="Conclusie"
                    onChange={(value) =>
                        onChange(
                            {
                                inclusie_in_zorgpad: value as BariPreMdoReportData['inclusie_in_zorgpad'],
                                verwijsbrief_nodig: null,
                            },
                            'inclusie_in_zorgpad'
                        )
                    }
                >
                    {mdoReportAdviceWithColor.map((item) => (
                        <Radio
                            disabled={adviceValueIsDisabled(item.value, caroPatient)}
                            key={item.value}
                            label={
                                <Badge
                                    color={
                                        !caroPatient.hasActivatedAccount && item.value === MdoReportAdvice.include
                                            ? 'neutral'
                                            : item.color
                                    }
                                    text={item.value}
                                />
                            }
                            value={item.value}
                        />
                    ))}
                </RadioGroupFormGroup>

                <TextAreaFormGroup
                    fieldKey="toelichting"
                    isRequired={true}
                    label="Toelichting"
                    placeholder="Typ je toelichting..."
                />
                {(data.inclusie_in_zorgpad === MdoReportAdvice.continueWithGuidance ||
                    data.inclusie_in_zorgpad === MdoReportAdvice.notYet) && (
                    <BooleanRadioGroupFormGroup
                        fieldKey="verwijsbrief_nodig"
                        isRequired={true}
                        label="Verwijsbrief nodig"
                        type="radio"
                    />
                )}
                {adviesAanFields.map((field) => (
                    <TextAreaFormGroup
                        fieldKey={field.fieldKey}
                        key={field.fieldKey}
                        label={field.label}
                        placeholder="Typ je advies..."
                    />
                ))}

                {data.inclusie_in_zorgpad === MdoReportAdvice.include && <AanvullendeConclusies />}
            </FormSection>
            <IndicatiesVoorVerwijzing />
            <AlertCheckbox
                checked={data.klaar_om_te_versturen}
                hasError={errors.has('klaar_om_te_versturen')}
                onChange={(checked) => onChange({ klaar_om_te_versturen: checked }, 'klaar_om_te_versturen')}
                text="Het verslag van het MDO is klaar om te versturen*"
                title="Klaar om te versturen"
                titleDanger="Geef alsjeblieft eerst aan of het verslag klaar is om te versturen"
            />
        </>
    );
}
